import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./LeftSidebar.module.css";
import ReactDOM from "react-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase/config";
import { useThemedIcon } from "../../../hooks/useThemedIcon";
import ShareModal from "../../../components/ShareModal/ShareModal";

import exportIconActiveLight from "../../../assets/export_active_light.svg";
import exportIconActiveDark from "../../../assets/export_active_dark.svg";

// Import icons
import ellipsisIconLight from "../../../assets/ellipsis_light.svg";
import ellipsisIconDark from "../../../assets/ellipsis_dark.svg";
import featureIconLight from "../../../assets/feature_light.svg";
import featureIconDark from "../../../assets/feature_dark.svg";
import dockToLeftIconLight from "../../../assets/dock_to_left_light.svg";
import dockToLeftIconDark from "../../../assets/dock_to_left_dark.svg";
import copyIconLight from "../../../assets/copy_message_light.svg";
import copyIconDark from "../../../assets/copy_message_dark.svg";
import checkIconLight from "../../../assets/check_messages_light.svg";
import checkIconDark from "../../../assets/check_messages_light.svg";
import chevronIconLight from "../../../assets/chevron_light.svg";
import chevronIconDark from "../../../assets/chevron_dark.svg";
import boxIconLight from "../../../assets/parametric_light.svg";
import boxIconDark from "../../../assets/parametric_dark.svg";
import loadingIcon from "../../../assets/loading.svg";
import exportIconLight from "../../../assets/export_light.svg";
import exportIconDark from "../../../assets/export_dark.svg";
import privateIconLight from "../../../assets/private_light.svg";
import publicIconLight from "../../../assets/public_light.svg";
import privateIconWhite from "../../../assets/private_white.svg";
import publicIconWhite from "../../../assets/public_white.svg";
import privateIconBlack from "../../../assets/private_black.svg";
import publicIconBlack from "../../../assets/public_black.svg";
import privateIconDark from "../../../assets/private_dark.svg";
import publicIconDark from "../../../assets/public_dark.svg";
import unfoldIconLight from "../../../assets/chevron_light.svg";
import unfoldIconDark from "../../../assets/chevron_dark.svg";

interface TreeItem {
  id: string;
  name: string;
  displayName?: string;
  type: "create" | "modify" | "delete";
  children?: TreeItem[];
  parentId?: string;
}

interface LeftSidebarProps {
  treeData: TreeItem[];
  onTreeItemSelect?: (itemId: string) => void;
  expandedItems: string[];
  onToggleExpanded: (itemId: string) => void;
  fileName: string;
  currentMeasurement?: number;

  onFileNameChange?: (newName: string) => void;
  onItemRename?: (itemId: string, newDisplayName: string) => void;
  getDisplayName?: (item: TreeItem) => string;
  onFileDelete?: () => void;
  projectId?: string;
  isSharedView?: boolean;
  onShareStatusChange?: () => void;
  isPendingSave?: boolean;
  pendingFeatureName?: string | null;
  onDockToggle?: () => void;
  onMobileClose?: () => void; // ADD THIS LINE
  colorValue?: string;
  onColorChange?: (value: string) => void;
  transparencyValue?: number;
  displayUnit?: string;
  onDisplayUnitChange?: (unit: string) => void;
  isPublic?: boolean;
  shareToken?: string;
  onExport?: (format: string) => void;
  selectedExportFormat?: string;
  onExportFormatChange?: (format: string) => void;
}

const ConfirmDeleteModal: React.FC<{
  isOpen: boolean;
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}> = ({ isOpen, fileName, onConfirm, onCancel, isDeleting = false }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Delete model</h3>
        <p className={styles.modalText}>
          This will delete <strong>{fileName}</strong>
        </p>
        <p className={styles.modalSubtext}>
          Are you sure you want to permanently delete this model and all its
          data? This action cannot be undone.
        </p>
        <div className={styles.modalButtons}>
          <button
            className={styles.modalDeleteButton}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                Deleting...
                <img
                  src={loadingIcon}
                  alt="Deleting"
                  className={styles.deletingIcon}
                />
              </>
            ) : (
              "Delete model"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
const LeftSidebar: React.FC<LeftSidebarProps> = ({
  treeData,
  onTreeItemSelect,
  expandedItems,
  onToggleExpanded,
  fileName,
  onFileNameChange,
  onItemRename,
  getDisplayName,
  onFileDelete,
  projectId,
  isSharedView = false,
  onShareStatusChange,
  isPendingSave = false,
  pendingFeatureName,
  onDockToggle,
  onMobileClose, // ADD THIS LINE
  colorValue = document.documentElement.classList.contains("dark")
    ? "6f6f6f"
    : "8d8d8d",
  onColorChange,
  displayUnit = "mm",
  onDisplayUnitChange,
  isPublic = false,
  shareToken,
  onExport,
  currentMeasurement,
  selectedExportFormat = ".STL",
  onExportFormatChange,
}) => {
  const { getIcon } = useThemedIcon();
  const boxIcon = getIcon(boxIconLight, boxIconDark);
  const chevronIcon = getIcon(chevronIconLight, chevronIconDark);
  const featureIcon = getIcon(featureIconLight, featureIconDark);
  const dockToLeftIcon = getIcon(dockToLeftIconLight, dockToLeftIconDark);
  const exportIcon = getIcon(exportIconLight, exportIconDark);
  const exportIconActive = getIcon(exportIconActiveLight, exportIconActiveDark);
  const privateIcon = privateIconLight; // Always use light version

  // Replace the existing privateIcon and publicIcon lines with:
  const privateIconDefault = getIcon(privateIconLight, privateIconDark);
  const publicIconDefault = getIcon(publicIconLight, publicIconDark);
  const privateIconHover = getIcon(privateIconBlack, privateIconWhite);
  const publicIconHover = getIcon(publicIconBlack, publicIconWhite);
  const unfoldIcon = getIcon(unfoldIconLight, unfoldIconDark);
  const copyIconShare = getIcon(copyIconLight, copyIconDark);
  const checkIconShare = getIcon(checkIconLight, checkIconDark);

  const [isEditingFileName, setIsEditingFileName] = useState(false);
  // Add after the other state declarations (around line 70)
  const [tooltipStates, setTooltipStates] = useState<{
    [key: string]: boolean;
  }>({});

  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [projectIsPublic, setProjectIsPublic] = useState(false);
  const [projectShareToken, setProjectShareToken] = useState<
    string | undefined
  >(undefined);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [localIsPublic, setLocalIsPublic] = useState(isPublic);
  const [localShareToken, setLocalShareToken] = useState(shareToken);
  const [isCopiedShare, setIsCopiedShare] = useState(false);
  const [hsvColor, setHsvColor] = useState({ h: 0, s: 100, v: 100 });
  const [isControlsScrolled, setIsControlsScrolled] = useState(false);

  const fileNameInputRef = useRef<HTMLInputElement>(null);
  const itemInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const treeViewRef = useRef<HTMLDivElement>(null);
  const colorDropdownRef = useRef<HTMLDivElement>(null);
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const shareDropdownRef = useRef<HTMLDivElement>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const hueSliderRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const activeColorDragRef = useRef<"saturation" | "hue" | null>(null);

  const handleItemRename = (itemId: string) => {
    setEditingItemId(itemId);
  };

  // Add these handler functions (around line 200, before the renderTreeItem function)
  const handleTooltipShow = useCallback((tooltipKey: string) => {
    setTooltipStates((prev) => ({ ...prev, [tooltipKey]: true }));
  }, []);

  const handleTooltipHide = useCallback((tooltipKey: string) => {
    setTooltipStates((prev) => ({ ...prev, [tooltipKey]: false }));
  }, []);

  useEffect(() => {
    const treeViewElement = treeViewRef.current;
    if (!treeViewElement) return;

    const handleScroll = () => {
      const scrollTop = treeViewElement.scrollTop;
      setIsScrolled(scrollTop > 0);

      // Check if scrolled enough to show controls border
      const scrollBottom =
        treeViewElement.scrollHeight -
        treeViewElement.scrollTop -
        treeViewElement.clientHeight;
      setIsControlsScrolled(scrollBottom > 5); // 5px threshold
    };

    treeViewElement.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      treeViewElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorDropdownRef.current &&
        !colorDropdownRef.current.contains(event.target as Node)
      ) {
        setIsColorDropdownOpen(false);
      }
      if (
        unitDropdownRef.current &&
        !unitDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUnitDropdownOpen(false);
      }
      if (
        shareDropdownRef.current &&
        !shareDropdownRef.current.contains(event.target as Node)
      ) {
        setIsShareDropdownOpen(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target as Node)
      ) {
        setIsExportDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setLocalIsPublic(isPublic);
    setLocalShareToken(shareToken);
  }, [isPublic, shareToken]);

  useEffect(() => {
    if (showShareModal && projectId) {
      loadProjectShareStatus();
    }
  }, [showShareModal, projectId]);

  const handleRename = () => {
    setIsEditingFileName(true);
  };

  const handleDeleteConfirm = async () => {
    if (!onFileDelete) return;
    setIsDeleting(true);
    try {
      await onFileDelete();
    } catch (error) {
      // console.error("Error deleting file:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleFileNameKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      setIsEditingFileName(false);
      if (onFileNameChange && fileNameInputRef.current) {
        const newValue = fileNameInputRef.current.value.trim();
        if (newValue) {
          onFileNameChange(newValue);
        }
      }
    } else if (event.key === "Escape") {
      setIsEditingFileName(false);
    }
  };

  const handleFileNameBlur = () => {
    setIsEditingFileName(false);
    if (onFileNameChange && fileNameInputRef.current) {
      const newValue = fileNameInputRef.current.value.trim();
      if (newValue) {
        onFileNameChange(newValue);
      }
    }
  };

  const loadProjectShareStatus = async () => {
    if (!projectId) return;
    try {
      const projectDoc = await getDoc(doc(db, "projects", projectId));
      if (projectDoc.exists()) {
        const data = projectDoc.data();
        setProjectIsPublic(data.isPublic || false);
        setProjectShareToken(data.shareToken);
      }
    } catch (error) {
      // console.error("Error loading project share status:", error);
    }
  };

  const handleItemNameKeyDown = (
    event: React.KeyboardEvent,
    itemId: string
  ) => {
    if (event.key === "Enter") {
      const input = itemInputRefs.current[itemId];
      if (input && onItemRename) {
        const newValue = input.value.trim();
        if (newValue) {
          onItemRename(itemId, newValue);
        }
      }
      setEditingItemId(null);
    } else if (event.key === "Escape") {
      setEditingItemId(null);
    }
  };

  const handleItemNameBlur = (itemId: string) => {
    const input = itemInputRefs.current[itemId];
    if (input && onItemRename) {
      const newValue = input.value.trim();
      if (newValue) {
        onItemRename(itemId, newValue);
      }
    }
    setEditingItemId(null);
  };

  const toggleExpanded = (id: string) => {
    onToggleExpanded(id);
  };

  const hsvToHex = (h: number, s: number, v: number): string => {
    const c = (v / 100) * (s / 100);
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v / 100 - c;

    let r = 0,
      g = 0,
      b = 0;
    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    const toHex = (n: number) =>
      Math.round((n + m) * 255)
        .toString(16)
        .padStart(2, "0");
    return `${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const hexToHsv = (hex: string): { h: number; s: number; v: number } => {
    const cleanHex = hex.replace("#", "").padEnd(6, "0");
    const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
    const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
    const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const diff = max - min;

    let h = 0;
    if (diff !== 0) {
      if (max === r) h = 60 * (((g - b) / diff) % 6);
      else if (max === g) h = 60 * ((b - r) / diff + 2);
      else h = 60 * ((r - g) / diff + 4);
      if (h < 0) h += 360;
    }

    const s = max === 0 ? 0 : (diff / max) * 100;
    const v = max * 100;

    return {
      h: s < 1 ? hsvColor.h : Math.round(h),
      s: Math.round(s),
      v: Math.round(v),
    };
  };

  useEffect(() => {
    if (isColorDropdownOpen) {
      const hsv = hexToHsv(colorValue);
      setHsvColor(hsv);
    }
  }, [isColorDropdownOpen, colorValue]);

  useEffect(() => {
    if (!activeColorDragRef.current) return;

    const handleMove = (clientX: number, clientY: number) => {
      if (
        activeColorDragRef.current === "saturation" &&
        colorPickerRef.current
      ) {
        const rect = colorPickerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

        const newHsv = { h: hsvColor.h, s: x * 100, v: (1 - y) * 100 };
        setHsvColor(newHsv);
        if (onColorChange) {
          onColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
        }
      }

      if (activeColorDragRef.current === "hue" && hueSliderRef.current) {
        const rect = hueSliderRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

        const newHsv = { h: x * 360, s: hsvColor.s, v: hsvColor.v };
        setHsvColor(newHsv);
        if (onColorChange) {
          onColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      activeColorDragRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleEnd);
    document.addEventListener("touchcancel", handleEnd);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "crosshair";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleEnd);
      document.removeEventListener("touchcancel", handleEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [activeColorDragRef.current, hsvColor, onColorChange]);

  const handleColorInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      setIsColorDropdownOpen(false);
      colorInputRef.current?.blur();
    }
  };

  const generateShareToken = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  const handleSharePrivateClick = async () => {
    if (!projectId) return;
    try {
      await updateDoc(doc(db, "projects", projectId), {
        isPublic: false,
        updatedAt: new Date(),
      });
      setLocalIsPublic(false);
      // Remove this line: setIsShareDropdownOpen(false);
      if (onShareStatusChange) {
        onShareStatusChange();
      }
    } catch (error) {
      // console.error("Error setting project to private:", error);
    }
  };

  const handleSharePublicClick = async () => {
    if (!projectId) return;
    try {
      let token = localShareToken;
      if (!token) {
        token = generateShareToken();
        setLocalShareToken(token);
      }
      await updateDoc(doc(db, "projects", projectId), {
        isPublic: true,
        shareToken: token,
        updatedAt: new Date(),
      });
      setLocalIsPublic(true);

      // Copy the share link
      const shareUrl = `${window.location.origin}/share/${token}`;
      navigator.clipboard.writeText(shareUrl);
      setIsCopiedShare(true);
      setTimeout(() => {
        setIsCopiedShare(false);
      }, 2000);

      if (onShareStatusChange) {
        onShareStatusChange();
      }
    } catch (error) {
      // console.error("Error setting project to public:", error);
    }
  };

  const handleCopyShareLink = () => {
    if (localShareToken) {
      const shareUrl = `${window.location.origin}/share/${localShareToken}`;
      navigator.clipboard.writeText(shareUrl);
      setIsCopiedShare(true);
      setTimeout(() => {
        setIsCopiedShare(false);
      }, 2000);
    }
  };

  const convertFromMm = (valueInMm: number, targetUnit: string): string => {
    const conversions: {
      [key: string]: { factor: number; decimals: number; label: string };
    } = {
      mm: { factor: 1.0, decimals: 2, label: "mm" },
      cm: { factor: 0.1, decimals: 2, label: "cm" },
      m: { factor: 0.001, decimals: 4, label: "m" },
      in: { factor: 1 / 25.4, decimals: 3, label: "in" },
      ft: { factor: 1 / 304.8, decimals: 4, label: "ft" },
    };

    const conv = conversions[targetUnit] || conversions["mm"];
    const converted = valueInMm * conv.factor;

    return `${converted.toFixed(conv.decimals)}${conv.label}`;
  };

  const renderTreeItem = (item: TreeItem, level: number = 0): JSX.Element => {
    const isExpanded = expandedItems.includes(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isEditing = editingItemId === item.id;
    const displayName = getDisplayName ? getDisplayName(item) : item.name;

    return (
      <>
        <div key={item.id} className={styles.treeItem}>
          <div
            className={`${styles.treeItemRow} ${
              isEditing ? styles.editing : ""
            }`}
            style={{
              paddingLeft: `${level * 40}px`,
              cursor: !isEditing && !isSharedView ? "pointer" : "default",
            }}
            onClick={() => {
              if (!isEditing && !isSharedView) {
                handleItemRename(item.id);
              }
            }}
          >
            {hasChildren && (
              <button
                className={styles.chevronButton}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(item.id);
                }}
              >
                <img
                  src={chevronIcon}
                  alt="Expand/Collapse"
                  className={`${styles.chevronIcon} ${
                    !isExpanded ? styles.chevronClosed : ""
                  }`}
                />
              </button>
            )}
            <div className={styles.itemContent}>
              <img
                src={featureIcon}
                alt="Feature"
                className={styles.itemIcon}
              />
              {isEditing ? (
                <input
                  ref={(el) => {
                    itemInputRefs.current[item.id] = el;
                    if (el) {
                      el.focus();
                      el.select();
                      el.setSelectionRange(0, el.value.length);
                    }
                  }}
                  type="text"
                  defaultValue={displayName}
                  onKeyDown={(e) => handleItemNameKeyDown(e, item.id)}
                  onBlur={() => handleItemNameBlur(item.id)}
                  className={styles.itemNameInput}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className={styles.itemText}>{displayName}</span>
              )}
            </div>
          </div>
        </div>
        {hasChildren &&
          isExpanded &&
          item.children?.map(
            (child): JSX.Element => renderTreeItem(child, level + 1)
          )}
      </>
    );
  };

  return (
    <>
      <div className={styles.sidebar}>
        {/* NEW: Add header like RightSidebar */}
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarHeaderTitle}>Features</span>
        </div>

        {/* COMMENTED OUT - File name, export, and share now handled elsewhere */}
        {/* <div
        className={`${styles.header} ${
          isScrolled ? styles.headerScrolled : ""
        }`}
      >
        <div
          className={`${styles.fileNameArea} ${
            isEditingFileName ? styles.editing : ""
          }`}
          onClick={
            !isEditingFileName && !isSharedView ? handleRename : undefined
          }
          style={{
            cursor:
              !isEditingFileName && !isSharedView ? "pointer" : "default",
          }}
        >
          <img src={boxIcon} alt="File" className={styles.fileNameIcon} />
          {isEditingFileName ? (
            <input
              ref={(el) => {
                (
                  fileNameInputRef as React.MutableRefObject<HTMLInputElement | null>
                ).current = el;
                if (el) {
                  el.focus();
                  el.select();
                  el.setSelectionRange(0, el.value.length);
                }
              }}
              type="text"
              defaultValue={fileName}
              onKeyDown={handleFileNameKeyDown}
              onBlur={handleFileNameBlur}
              className={styles.fileNameInput}
              autoFocus
            />
          ) : (
            <span className={styles.fileNameText}>{fileName}</span>
          )}
        </div>

        <div className={styles.headerButtonContainer} ref={exportDropdownRef}>
          <button
            className={`${styles.headerIconButton} ${styles.exportButton} ${
              isExportDropdownOpen ? styles.headerIconButtonActive : ""
            }`}
            onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
            onMouseEnter={() => handleTooltipShow("export")}
            onMouseLeave={() => handleTooltipHide("export")}
          >
            <img
              src={
                isExportDropdownOpen || tooltipStates["export"]
                  ? exportIconActive
                  : exportIcon
              }
              alt="Export"
              className={styles.headerButtonIcon}
            />
            {tooltipStates["export"] && !isExportDropdownOpen && (
              <div className={styles.tooltip}>Export</div>
            )}
          </button>
          {isExportDropdownOpen && (
            <div className={styles.headerDropdownMenu}>
              <div
                className={styles.exportOption}
                onClick={() => {
                  if (onExportFormatChange) onExportFormatChange(".STL");
                  if (onExport) onExport();
                }}
              >
                <div className={styles.exportOptionMain}>.STL</div>
                <div className={styles.exportOptionSub}>3D Printing</div>
              </div>
              <div
                className={styles.exportOption}
                onClick={() => {
                  if (onExportFormatChange) onExportFormatChange(".STEP");
                  if (onExport) onExport();
                }}
              >
                <div className={styles.exportOptionMain}>.STEP</div>
                <div className={styles.exportOptionSub}>CAD Exchange</div>
              </div>
            </div>
          )}
        </div>

        {!isSharedView && (
          <div
            className={styles.headerButtonContainer}
            ref={shareDropdownRef}
          >
            <button
              className={`${styles.headerIconButton} ${
                isShareDropdownOpen ? styles.headerIconButtonActive : ""
              }`}
              onClick={() => setIsShareDropdownOpen(!isShareDropdownOpen)}
              onMouseEnter={() => handleTooltipShow("share")}
              onMouseLeave={() => handleTooltipHide("share")}
            >
              <img
                src={
                  isShareDropdownOpen || tooltipStates["share"]
                    ? localIsPublic
                      ? publicIconHover
                      : privateIconHover
                    : localIsPublic
                      ? publicIconDefault
                      : privateIconDefault
                }
                alt={localIsPublic ? "Public" : "Private"}
                className={styles.headerButtonIcon}
              />
              {tooltipStates["share"] && !isShareDropdownOpen && (
                <div className={styles.tooltip}>
                  {localIsPublic ? "Public" : "Private"}
                </div>
              )}
            </button>
            {isShareDropdownOpen && (
              <div className={styles.headerDropdownMenu}>
                <div
                  className={`${styles.shareOption} ${
                    !localIsPublic ? styles.shareOptionSelected : ""
                  }`}
                  onClick={handleSharePrivateClick}
                >
                  <div className={styles.shareOptionContent}>
                    <div className={styles.shareOptionMain}>Private</div>
                    <div className={styles.shareOptionSub}>
                      Only you can edit
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.shareOption} ${
                    localIsPublic ? styles.shareOptionSelected : ""
                  }`}
                  onClick={handleSharePublicClick}
                >
                  <div className={styles.shareOptionContent}>
                    <div className={styles.shareOptionMain}>Public</div>
                    <div className={styles.shareOptionSub}>
                      Anyone with the link can view
                    </div>
                    {localIsPublic && localShareToken && (
                      <div
                        className={styles.shareLinkBox}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyShareLink();
                        }}
                      >
                        <div className={styles.shareLinkText}>
                          {`${window.location.origin}/share/${localShareToken}`}
                        </div>
                        <div className={styles.copyShareIcon}>
                          <img
                            src={
                              isCopiedShare ? checkIconShare : copyIconShare
                            }
                            alt={isCopiedShare ? "Copied" : "Copy"}
                            className={styles.buttonIcon}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {onDockToggle && (
          <button
            className={styles.mobileCloseButton}
            onClick={onMobileClose || onDockToggle}
          >
            <img
              src={dockToLeftIcon}
              alt="Close sidebar"
              className={styles.mobileCloseIcon}
            />
          </button>
        )}
      </div> */}

        <div className={styles.content}>
          <div className={styles.treeView} ref={treeViewRef}>
            {treeData.length === 0 ? (
              <div className={styles.emptyState}>No features yet</div>
            ) : (
              treeData.map((item) => {
                const isPending =
                  pendingFeatureName &&
                  (item.name === pendingFeatureName ||
                    item.displayName === pendingFeatureName);
                if (isPending) return null;

                if (item.children && item.children.length > 0) {
                  const filteredChildren = item.children.filter((child) => {
                    const childIsPending =
                      pendingFeatureName &&
                      (child.name === pendingFeatureName ||
                        child.displayName === pendingFeatureName);
                    return !childIsPending;
                  });
                  return renderTreeItem({
                    ...item,
                    children: filteredChildren,
                  });
                }
                return renderTreeItem(item);
              })
            )}
          </div>
        </div>
      </div>

      {typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <ConfirmDeleteModal
            isOpen={showDeleteModal}
            fileName={fileName}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            isDeleting={isDeleting}
          />,
          document.body
        )}

      {typeof document !== "undefined" &&
        projectId &&
        ReactDOM.createPortal(
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            projectId={projectId}
            projectName={fileName}
            isPublic={projectIsPublic}
            shareToken={projectShareToken}
            onShareStatusChange={() => {
              loadProjectShareStatus();
              if (onShareStatusChange) {
                onShareStatusChange();
              }
            }}
          />,
          document.body
        )}
    </>
  );
};

export default LeftSidebar;
